import { useEffect } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { useParams, useNavigate } from 'react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { Save } from 'lucide-react';
import { toast } from 'sonner';

import { type Voice, type VoiceProviderConfig, upsertVoiceSchema } from '@intone/shared';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { FormSliderField } from '@/components/form-fields';

import { useVoice, useUpsertVoice } from '@/entities/voice';
import { useUnsavedChanges } from '../lib/FormStatusProvider';
import { getBrandAssets } from '@/lib/branding';
import { VoicePreview } from './VoicePreview';

const defaultValues: Voice = {
  alt_key: '',
  name: '',
  provider: 'elevenlabs',
  ext_id: null,
  provider_config: {
    speed: 1,
    style: 0,
    stability: 0.5,
    similarity_boost: 0.95,
    use_speaker_boost:  null,
  } as VoiceProviderConfig,
  model: 'eleven_flash_v2_5',
  company_id: null,
};

const mapServerData = (data: Voice | undefined) => ({
  ...defaultValues,
  ...data,
  provider_config: { ...defaultValues.provider_config, ...data?.provider_config }
});

const ELEVENLABS_MODELS = [
  'eleven_v3',
  'eleven_ttv_v3',
  'eleven_multilingual_v2',
  'eleven_flash_v2_5',
  'eleven_flash_v2',
  'eleven_multilingual_sts_v2',
  'eleven_multilingual_ttv_v2',
  'eleven_english_sts_v2',
  'scribe_v2_realtime',
  'scribe_v2',
  'scribe_v1',
  'eleven_text_to_sound_v2',
  'music_v1',
];

const RIME_MODELS = [
  'arcana',
  'mistv2',
  'mist',
];

const CATEGORY_DATA = {
  elevenlabs: {
    label: 'ElevenLabs',
    models: ELEVENLABS_MODELS,
  },
  rime: {
    label: 'Rime',
    models: RIME_MODELS,
  }
};

const providerOptions = Object.entries(CATEGORY_DATA).map(([value, info]) => ({
  value,
  label: info.label
}));

export function VoiceEditor() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isNewVoice = id === 'new';
  const selectedVoiceId = (!isNewVoice && id) ? Number(id) : null;

  const { data: voice, isLoading, isSuccess, isError, error } = useVoice(selectedVoiceId, isNewVoice);

  const upsertVoice = useUpsertVoice();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isDirty }
  } = useForm({
    resolver: zodResolver(upsertVoiceSchema),
    values: isNewVoice ? defaultValues : mapServerData(voice),
  });

  const { setIsDirty } = useUnsavedChanges();

  useEffect(() => {
    setIsDirty(isDirty);
    return () => setIsDirty(false);
  }, [isDirty, setIsDirty]);

  const selectedProvider = useWatch({ control, name: 'provider' });

  const modelOptions = selectedProvider ? CATEGORY_DATA[selectedProvider as keyof typeof CATEGORY_DATA].models : [];

  const onSubmit = (formData: Voice) => {
    upsertVoice.mutate(
      formData,
      {
        onSuccess: (result) => {
          if (isNewVoice) {
            setIsDirty(false);
            navigate(`/voices/${result.id}`, { replace: true });
          }
          toast.success('Voice saved successfully', { id: 'voice-save' });
        },
        onError: (err: Error) => {
          toast.error(err.message || 'Failed to save Voice', { id: 'voice-save-error' });
        },
      }
    );
  };

  if (!isSuccess && !isNewVoice) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
        {!selectedVoiceId && <p className="text-body">Select a voice to start editing or create a new one</p>}
        {isLoading && <p className="text-body animate-pulse">Loading voice details...</p>}
        {isError && <p className="text-body text-destructive">Error: {error.message}</p>}
      </div>
    );
  }

  const isDefaultVoice = !isNewVoice && voice?.company_id === null;
  const isClonedDefault = !isNewVoice && voice?.cloned_from_default === true;
  const frozen = isDefaultVoice || upsertVoice.isPending;
  const { brandName } = getBrandAssets();

  return (
    <form className="px-4 pb-4 space-y-3" onSubmit={handleSubmit(onSubmit)}>
      {/* General Configuration Card */}
      <Card className="border-gray-300">
        <CardHeader className="bg-gray-50 border-b border-gray-300 py-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-body font-semibold">
              {isNewVoice ? 'Create New Voice' : 'Voice Settings'}
            </CardTitle>
            {!isDefaultVoice && (
              <div className="flex items-center gap-2">
                {isDirty && (
                  <span className="text-detail text-destructive">You have unsaved changes</span>
                )}
                <Button
                  type="submit"
                  size="sm"
                  disabled={upsertVoice.isPending}
                >
                  <Save className="w-3 h-3 mr-2" />
                  {upsertVoice.isPending ? 'Saving...' : 'Save Voice'}
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          {isDefaultVoice ? (
            <div className="grid gap-1">
              <Label className="font-medium text-gray-700">Provider</Label>
              <p className="text-body text-gray-900 py-1">{brandName}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1">
                <Label htmlFor="voice_name" className="font-medium text-gray-700">Name</Label>
                <Input
                  id="voice_name"
                  {...register('name')}
                  placeholder="Voice name"
                  disabled={upsertVoice.isPending}
                />
                <span className="text-detail text-destructive h-4">
                  {errors.name && errors.name.message}
                </span>
              </div>

              {!isClonedDefault && (
                <div className="grid gap-1">
                  <Label htmlFor="voice_ext_id" className="font-medium text-gray-700">External ID (Voice ID)</Label>
                  <Input
                    id="voice_ext_id"
                    {...register('ext_id')}
                    placeholder="External ID from provider"
                    disabled={upsertVoice.isPending}
                  />
                  <span className="text-detail text-destructive h-4">
                    {errors.ext_id && errors.ext_id.message}
                  </span>
                </div>
              )}

              <div className="grid gap-1">
                <Label htmlFor="voice_provider" className="font-medium text-gray-700">Provider</Label>
                {isClonedDefault ? (
                  <p className="text-body text-gray-900 py-1">{brandName}</p>
                ) : (
                  <>
                    <Controller
                      name="provider"
                      control={control}
                      render={({ field: { onChange, value, ref } }) => (
                        <Select
                          value={value}
                          onValueChange={(val) => {
                            onChange(val);
                            setValue('model', '');
                          }}
                        >
                          <SelectTrigger id="voice_provider" ref={ref} disabled={upsertVoice.isPending}>
                            <SelectValue placeholder="Select provider" />
                          </SelectTrigger>
                          <SelectContent>
                            {providerOptions.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                    <span className="text-detail text-destructive h-4">
                      {errors.provider && errors.provider.message}
                    </span>
                  </>
                )}
              </div>

              <div className="grid gap-1">
                <Label htmlFor="voice_model" className="font-medium text-gray-700">Model</Label>
                <Controller
                  name="model"
                  control={control}
                  render={({ field: { onChange, value, ref } }) => (
                    <Select value={value || ''} onValueChange={onChange}>
                      <SelectTrigger
                        id="voice_model"
                        ref={ref}
                        disabled={!modelOptions.length || upsertVoice.isPending}
                      >
                        <SelectValue placeholder="Select model" />
                      </SelectTrigger>
                      <SelectContent>
                        {modelOptions.map(opt => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                <span className="text-detail text-destructive h-4">
                  {errors.model && errors.model.message}
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Provider Configuration Card */}
      <Card className="border-gray-300">
        <CardHeader className="bg-gray-50 border-b border-gray-300 py-2">
          <CardTitle className="text-body font-semibold">Provider Configuration</CardTitle>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Controller
              name="provider_config.speed"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormSliderField id="voice_speed" label="Speed" value={value as number} onChange={onChange} rangeMin={0.5} rangeMax={1.3} step={0.01} formatValue={String} disabled={frozen} />
              )}
            />
            <Controller
              name="provider_config.style"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormSliderField id="voice_style" label="Style" value={value as number} onChange={onChange} rangeMin={0} rangeMax={1} step={0.01} formatValue={String} disabled={frozen} />
              )}
            />
            <Controller
              name="provider_config.stability"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormSliderField id="voice_stability" label="Stability" value={value as number} onChange={onChange} rangeMin={0} rangeMax={1} step={0.01} formatValue={String} disabled={frozen} />
              )}
            />
            <Controller
              name="provider_config.similarity_boost"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormSliderField id="voice_similarity_boost" label="Similarity Boost" value={value as number} onChange={onChange} rangeMin={0} rangeMax={1} step={0.01} formatValue={String} disabled={frozen} />
              )}
            />
            <div className="flex items-center gap-2">
              <Controller
                name="provider_config.use_speaker_boost"
                control={control}
                render={({ field }) => (
                  <Switch
                    id="speaker_boost"
                    checked={field.value || false}
                    onCheckedChange={(checked) => field.onChange(checked)}
                    disabled={frozen}
                  />
                )}
              />
              <Label htmlFor="speaker_boost" className="font-medium text-gray-700">Speaker Boost</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {selectedVoiceId && (
        <VoicePreview voiceId={selectedVoiceId} provider={selectedProvider} />
      )}
    </form>
  );
}